import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/index';
import { StoreKey } from '@/configs/storeKeys';
import {
	CreatorOptions,
	CreatorSource,
	CreatorState,
	CreatorStep,
	SourceItemData,
} from '@/types/creator';
import { getAvailableAttributes } from '@/utils/creatorUtils';
import { AttributeValue } from '@/api/AttributeValueApi';
import { OrderItem } from '@/api/OrderApi';
import { ProductType } from '@/api/ProductTypeApi';
import { Product } from '@/api/ProductApi';
import { UUID } from '@/types/types';
import { OfferItem } from '@/api/OfferApi';

const initialState: CreatorState = {
	productType: null,
	matchedProduct: null,
	availableProducts: [],
	attributesOrder: [],
	selectedValues: {},
	availableAttributes: [],
	sourceItemData: {
		id: null,
		quantity: 1,
		deliveryDate: new Date().toISOString(),
		comment: '',
		shippingMethod: null,
		shippingPriority: null,
		attachments: [],
	},
	options: {
		step: CreatorStep.ATTRIBUTES,
		source: CreatorSource.ORDER,
	},
};

export const creatorSlice = createSlice({
	name: StoreKey.CREATOR,
	initialState,
	reducers: {
		updateCreator: (
			state,
			{ payload }: PayloadAction<Partial<CreatorState>>
		) => {
			Object.assign(state, payload);
		},
		initialize: (
			state,
			{
				payload,
			}: PayloadAction<{
				productType: ProductType;
				orderItem: OrderItem | null;
				offerItem: OfferItem | null;
				products: Product[];
			}>
		) => {
			const { productType, orderItem, offerItem, products } = payload;

			state.productType = productType;
			state.availableProducts = products;

			state.selectedValues = productType.attributes.reduce<
				CreatorState['selectedValues']
			>((acc, attribute) => {
				const selectedValue = orderItem
					? orderItem.product.attributeValues.find(
							(val) => val.attribute.id === attribute.id
						)
					: null;

				acc[attribute.id] = selectedValue || null;
				return acc;
			}, {});

			state.availableAttributes = getAvailableAttributes(products);
			state.availableAttributes.forEach((attribute) => {
				state.attributesOrder.push(attribute.id);
			});

			const sourceItem = orderItem || offerItem;

			if (sourceItem) {
				state.sourceItemData.id = sourceItem.id;
				state.sourceItemData.quantity = sourceItem.quantity;
				state.sourceItemData.comment = sourceItem.comment;
				state.sourceItemData.deliveryDate = sourceItem.deliveryDate;
				state.sourceItemData.shippingMethod = sourceItem.shippingMethod;
				state.sourceItemData.shippingPriority = sourceItem.shippingPriority;
				state.matchedProduct = sourceItem.product;
				state.options.step = CreatorStep.SUMMARY;
			}
		},
		selectAttributeValue: (
			state,
			{ payload }: PayloadAction<AttributeValue>
		) => {
			state.selectedValues[payload.attribute.id] = payload;

			const level = state.availableAttributes.findIndex(
				(attribute) => attribute.id === payload.attribute.id
			);
			if (level === -1) return;

			for (let idx = level + 1; idx < state.availableAttributes.length; idx++) {
				const attribute = state.availableAttributes[idx];
				const selectedValuesAbove = Object.entries(state.selectedValues)
					.filter(([attributeId]) =>
						state.attributesOrder
							.slice(0, idx)
							.some((attr) => attr === attributeId)
					)
					.map((entry) => entry[1])
					.filter((value) => !!value);

				for (const creatorAttributeValue of attribute.filteredValues) {
					const isMatched = state.availableProducts.some((product) => {
						return [...selectedValuesAbove, creatorAttributeValue].every(
							(value) =>
								product.attributeValues.some((av) => av.id === value.id)
						);
					});

					creatorAttributeValue.disabled = !isMatched;
				}

				const currentSelected = state.selectedValues[attribute.id];
				const isStillAvailable =
					currentSelected &&
					attribute.filteredValues.some(
						(val) => val.id === currentSelected.id && !val.disabled
					);

				if (!isStillAvailable) state.selectedValues[attribute.id] = null;
			}
		},
		updateProduct: (state, { payload }: PayloadAction<Partial<Product>>) => {
			if (!state.matchedProduct) return;

			Object.assign(state.matchedProduct, payload);
		},
		updateOptions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorOptions>>
		) => {
			Object.assign(state.options, payload);
		},
		updateSourceItemData: (
			state,
			{ payload }: PayloadAction<Partial<SourceItemData>>
		) => {
			Object.assign(state.sourceItemData, payload);
		},

		removeAttachmentFromSourceItemData: (
			state,
			{ payload }: PayloadAction<UUID>
		) => {
			state.sourceItemData.attachments =
				state.sourceItemData.attachments.filter((att) => att.id !== payload);
		},
		resetCreator: (state) => {
			Object.assign(state, initialState);
		},
	},
});

export const creatorActions = creatorSlice.actions;

export const selectCreatorState = (state: RootState) => state.creator;
export default creatorSlice.reducer;
